import { formatCnpj } from "utils/federal-tax-id";
import { NEVER_SUCCEEDED, WorstDelay } from "./aggregate";

/**
 * Formatação compartilhada do painel. Criado no T9 (plano v2, 2026-08-11)
 * porque `formatFederalTaxId` vivia solto em `CnpjPage.tsx` e a lista do
 * portfólio passou a precisar do mesmo formato — e um segundo formatador
 * copiado seria a maneira mais fácil das duas telas divergirem.
 *
 * Confirmado antes de criar (instrução da T9): não há formatador de CNPJ
 * compartilhado no app. `src/utils/is-valid-cnpj.ts` valida, não formata.
 * (Desde 28/09/2026 existe: `src/utils/federal-tax-id.ts`, ClickUp 86akhub9e.)
 */

/**
 * `48785828009428` -> `48.785.828/0094-28`. Delega para o formatador único
 * (`utils/federal-tax-id`): fora de 14 dígitos devolve como veio.
 *
 * Antes completava com zeros à esquerda (`padStart(14, "0")`). Medido em
 * 28/09/2026: o dado vem de `organizations.federal_tax_id` (varchar); os 92
 * CNPJs que começam com zero estão gravados com os 14 caracteres e nenhum
 * valor tem entre 1 e 13 dígitos. O padding não restaurava nada, só inventaria
 * um CNPJ se aparecesse um valor curto.
 */
export function formatFederalTaxId(value: string): string {
  return formatCnpj(value);
}

/**
 * Janela de apuração de sucesso do backend, espelhada aqui só para escrever o
 * rótulo do pior caso.
 *
 * Fonte: `DEFAULT_PREVIOUS_FROM_DAYS` em
 * `conducco-admin-backend/src/modules/operational-panel/services/activity-health-service.ts`
 * (120 dias, o mesmo valor que `WIDE_WINDOW_DAYS` em `aggregate-health.ts`).
 * **Se a janela mudar lá, este número fica errado aqui** — o payload não a
 * carrega, então não há como derivá-la. Duplicação consciente, mesmo padrão
 * já aceito entre `config/cadence.ts` e `schedule_extraction.py` no backend.
 */
const SUCCESS_WINDOW_DAYS = 120;

/**
 * Renderiza o pior atraso de um CNPJ/workspace.
 *
 * O pior caso é texto, não número: não pode ser confundido com um valor alto de
 * dias nem com a ausência de dado (T3).
 *
 * **Por que não diz mais "nunca"** (bug reportado em 24/08/2026, workspace
 * GALCORR BUYER `44342186000132`): a tela mostrava dois vistos verdes de sucesso
 * e "Desde último sucesso: nunca" na mesma linha. `neverSucceeded`, que produz
 * este caso, não significa "nunca entregou" — significa "nenhuma execução
 * resolveu SUCCESS dentro da janela de apuração". Aquele workspace tinha
 * "Regras de contrato" com sucesso real em 04/09/2025, 354 dias antes: fora da
 * janela, não inexistente.
 *
 * O payload não distingue "nunca entregou em toda a história" de "entregou antes
 * da janela" — os dois chegam como `neverSucceeded: true`. `> 120 dias` é
 * verdadeiro nos dois casos; "nunca" só no primeiro, e afirmava mais do que o
 * dado sustenta. Distinguir os dois exige um campo novo no backend (consulta sem
 * janela), fora do escopo desta correção.
 */
export function formatWorstDelay(delay: WorstDelay): string | null {
  if (delay === NEVER_SUCCEEDED) return `> ${SUCCESS_WINDOW_DAYS} dias`;
  if (delay === undefined) return null;
  if (delay === 0) return "hoje";
  return `${delay} dia${delay > 1 ? "s" : ""}`;
}

/** `2026-07-01T14:01:00Z` -> `01/07`. Só dia e mês: o ano polui a linha e quase nunca importa. */
export function formatShortDate(iso: string): string {
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    timeZone: "America/Sao_Paulo",
  }).format(new Date(iso));
}

// `describeActivity` e `describeQbem` (T8/T4b) removidos no T9 (v2.1,
// 2026-08-19): eram exclusivos da `PortfolioPage.tsx` antiga (linha do
// CNPJ com atividade em destaque + métricas do QBem), que não existe mais
// na tela nova. `CnpjPage.tsx`/`WorkspacePage.tsx` nunca os usaram —
// confirmado por grep antes de remover.
